import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Typography,
  SelectChangeEvent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import config from "./config";
import { useNavigate } from "react-router-dom";

// Enums con valores numéricos
enum Price {
  Gratis = 0,
  Economico = 1,
  Moderado = 2,
  Costoso = 3,
}

enum PlaceType {
  Plaza = 0,
  ParqueTematico = 1,
  Campo = 2,
  JuegosNiños = 3,
  CamasElasticas = 4,
  ParqueAcuatico = 5,
  Museo = 6,
  Granja = 7,
  ParqueNatural = 8,
  EscapeRooms = 9,
  Restaurante = 10,
  Zoologicos = 11,
  Cine = 12,
  Arcade = 13,
  Shopping = 14,
}

const Create = () => {
  // Estado para almacenar los datos del formulario
  const [place, setPlace] = useState<{
    id: string;
    name: string;
    description: string;
    lat: number;
    longi: number;
    price: Price;
    placeType: PlaceType;
    webSite: string;
    direccion: string;
    calificacion: number;
    urlGoogle: string;
    horarios: string[];
    fotos: string[];
    aprobado: boolean;
  }>({
    id: "", // Guid se generará en el backend
    name: "",
    description: "",
    lat: 0,
    longi: 0,
    price: Price.Economico, // Usa el valor numérico del enum
    placeType: PlaceType.Plaza, // Usa el valor numérico del enum
    webSite: "",
    direccion: "",
    calificacion: 0,
    urlGoogle: "",
    horarios: [],
    fotos: [], // Inicializado como array vacío
    aprobado: false,
  });
  interface NominatimResult {
    display_name: string;
    lat: string;
    lon: string;
  }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = React.useState(false);
  const [modalMessage, setModalMessage] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [uploadingImages, setUploadingImages] = useState(false); // Nuevo estado para controlar la carga de imágenes
  
  const handleGenerateDescription = async () => {
    if (!place.name.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${config.backendUrl}/api/Places/DescripcionPorIA?lugar=${encodeURIComponent(place.name)}`
      );
      
      if (!response.ok) {
        throw new Error('Error al generar la descripción');
      }
      
      place.description = await response.text();
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    if (!target.name) return; // Asegurar que `name` existe

    setPlace((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  };

  // Manejar cambios en los arrays (Horarios)
  const handleArrayChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: "horarios",
    index: number
  ) => {
    const newArray = [...place[field]];
    newArray[index] = e.target.value;
    setPlace((prev) => ({
      ...prev,
      [field]: newArray,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    const { name, value } = e.target;
    setPlace((prev) => ({
      ...prev,
      [name]: Number(value), // Convierte el valor a número
    }));
  };

  // Agregar un nuevo elemento a los arrays (Horarios)
  const addArrayItem = (field: "horarios") => {
    setPlace((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  // Eliminar un elemento de los arrays (Horarios)
  const removeArrayItem = (field: "horarios", index: number) => {
    const newArray = place[field].filter((_, i) => i !== index);
    setPlace((prev) => ({
      ...prev,
      [field]: newArray,
    }));
  };

  // Manejar la subida de imágenes
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const uploadedUrls: string[] = [];
    setUploadingImages(true); // Activar el indicador de carga

    try {
      // Subir cada imagen al servidor
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${config.backendUrl}/api/Places/upload-image`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.fileUrl); // Guardar la URL de la imagen
        } else {
          console.error("Error al subir la imagen:", file.name);
        }
      }

      // Actualizar el estado `place.fotos` con las nuevas URLs
      setPlace((prev) => ({
        ...prev,
        fotos: [...prev.fotos, ...uploadedUrls],
      }));
    } catch (error) {
      console.error("Error al subir la imagen:", error);
    } finally {
      setUploadingImages(false); // Desactivar el indicador de carga
    }
  };

  // Eliminar una foto del array
  const removeFoto = (index: number) => {
    const newFotos = place.fotos.filter((_, i) => i !== index);
    setPlace((prev) => ({
      ...prev,
      fotos: newFotos,
    }));
  };

  const navigate = useNavigate();
  const handleClose = () => {
    setOpen(false);
    if (success) {
      navigate('/');
    }
  }
  
  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Datos enviados:", place);
    place.id = crypto.randomUUID();

    const res = await fetch(`${config.backendUrl}/api/places`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(place),
    });

    if (res.ok) {
      setModalMessage("Lugar creado exitosamente, lo analizaremos y pronto lo veras en la web");
      setSuccess(true);
    } else {
      setModalMessage("Error al crear el lugar");
      setSuccess(false);
    }
    setOpen(true);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]); 

  // Buscar direcciones usando Nominatim
  const handleSearch = async () => {
    if (!searchQuery) return;

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      searchQuery
    )}`;

    try {
      const response = await fetch(url);
      const data: NominatimResult[] = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.error("Error al buscar direcciones:", error);
    }
  };

  // Seleccionar una dirección de las sugerencias
  const handleSelectAddress = (selected: NominatimResult) => {
    setPlace((prev) => ({
      ...prev,
      name: selected.display_name,
      lat: parseFloat(selected.lat),
      longi: parseFloat(selected.lon),
      direccion: selected.display_name,
    }));
    setSuggestions([]); // Limpiar sugerencias
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600, margin: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Crear nuevo lugar
      </Typography>
      
      {/* Buscador de direcciones */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Buscar dirección"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          margin="normal"
        />
        <Button onClick={handleSearch} variant="contained" sx={{ mt: 1 }}>
          Buscar
        </Button>
        {suggestions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <h3>Seleccione la dirección</h3>
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                onClick={() => handleSelectAddress(suggestion)}
                sx={{ p: 2, border: "1px solid #ccc", cursor: "pointer", mb: 1 }}
              >
                <Typography>{suggestion.display_name}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      
      {/* Nombre */}
      <TextField
        fullWidth
        label="Nombre"
        name="name"
        value={place.name}
        onChange={handleChange}
        margin="normal"
        required
      />
      {place.name.trim() && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateDescription}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Generando...' : 'Generar descripción por IA'}
          </Button>
        </Box>
      )}
      
      {/* Descripción */}
      <TextField
        fullWidth
        label="Descripción"
        name="description"
        value={place.description}
        onChange={handleChange}
        margin="normal"
        multiline
        rows={4}
      />

      {/* Latitud y Longitud */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          fullWidth
          label="Latitud"
          name="lat"
          type="number"
          value={place.lat}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Longitud"
          name="longi"
          type="number"
          value={place.longi}
          onChange={handleChange}
          margin="normal"
        />
      </Box>

      {/* Precio */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Precio</InputLabel>
        <Select
          name="price"
          value={place.price}
          onChange={handleSelectChange}
          label="Precio"
        >
          {Object.values(Price)
            .filter((value) => typeof value === "number")
            .map((price) => (
              <MenuItem key={price} value={price}>
                {Price[price]}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Tipo de Lugar */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Tipo de Lugar</InputLabel>
        <Select
          name="placeType"
          value={place.placeType}
          onChange={handleSelectChange}
          label="Tipo de Lugar"
        >
          {Object.values(PlaceType)
            .filter((value) => typeof value === "number")
            .map((type) => (
              <MenuItem key={type} value={type}>
                {PlaceType[type]}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Sitio Web */}
      <TextField
        fullWidth
        label="Sitio Web"
        name="webSite"
        value={place.webSite}
        onChange={handleChange}
        margin="normal"
      />

      {/* Dirección */}
      <TextField
        fullWidth
        label="Dirección"
        name="direccion"
        value={place.direccion}
        onChange={handleChange}
        margin="normal"
      />

      {/* URL de Google */}
      <TextField
        fullWidth
        label="URL de Google"
        name="urlGoogle"
        value={place.urlGoogle}
        onChange={handleChange}
        margin="normal"
      />

      {/* Horarios */}
      <Typography variant="h6" gutterBottom>
        Horarios
      </Typography>
      {place.horarios.map((horario, index) => (
        <Box key={index} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            label={`Horario ${index + 1}`}
            value={horario}
            onChange={(e) => handleArrayChange(e, "horarios", index)}
            margin="normal"
          />
          <Button onClick={() => removeArrayItem("horarios", index)} color="error">
            Eliminar
          </Button>
        </Box>
      ))}
      <Button onClick={() => addArrayItem("horarios")} variant="outlined" sx={{ mt: 1 }}>
        Agregar Horario
      </Button>

      {/* Fotos */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Fotos
        </Typography>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleImageUpload(e)}
          disabled={uploadingImages}
        />
        {uploadingImages && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Subiendo imágenes...
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        {place.fotos.map((foto, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <img
              src={`${config.backendUrl}${foto}`}
              alt={`Foto ${index + 1}`}
              style={{ width: 100, height: 100, objectFit: "cover" }}
            />
            <Button onClick={() => removeFoto(index)} color="error">
              Eliminar
            </Button>
          </Box>
        ))}
      </Box>

      {/* Botón de envío */}
      <Button type="submit" variant="contained" sx={{ mt: 3 }}>
        Guardar
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{success ? "¡Éxito!" : "Error"}</DialogTitle>
        <DialogContent>
          {modalMessage}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color={success ? "primary" : "error"}>
            {success ? "Aceptar" : "Cerrar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Create;